/**
 * "Question N - {label}" numbering for Text Area fields of a Scorecard.
 *
 * Used in two places:
 *  - Saving: the Scorecard builder stores the prefixed labels in the template JSON.
 *  - Display: Scorecard Preview and the generated Scorecard form (covers templates
 *    saved before numbering existed).
 *
 * Rules:
 *  - Only TextArea elements are numbered; every other element is returned untouched
 *    and never moves the counter.
 *  - Numbers follow the order of the elements in the form data, which is the order
 *    they are rendered in, so re-ordering / deleting re-numbers the questions.
 *  - Numbering stops at MAX_QUESTIONS. Later Text Areas keep their label without prefix.
 *  - Idempotent: an existing "Question N - " prefix is replaced, never doubled, so
 *    already-numbered (saved) templates can safely be numbered again.
 *  - The input data is never mutated: changed elements are shallow copies.
 */

export const TEXT_AREA_ELEMENT = 'TextArea';
export const MAX_QUESTIONS = 5;
export const QUESTION_PREFIX = 'Question';

export const isTextArea = (element) => !!element && element.element === TEXT_AREA_ELEMENT;

// Leading opening tag of a rich-text label, e.g. "<p>" in "<p>Strengths</p>".
const LEADING_OPEN_TAG_REGEX = /^\s*<[a-z][^>]*>/i;

// Existing "Question N - " prefix (the rich text editor may store spaces as &nbsp;).
// const SPACE = '(?:\\s|&nbsp;)';
// const EXISTING_PREFIX_REGEX = new RegExp(`^${SPACE}*${QUESTION_PREFIX}${SPACE}+\\d+${SPACE}*-${SPACE}*`, 'i');
// Existing "Question:N" prefix (handles spaces and HTML non-breaking spaces)
const SPACE = '(?:\\s|&nbsp;)';
const EXISTING_PREFIX_REGEX = new RegExp(`^${SPACE}*${QUESTION_PREFIX}${SPACE}*:${SPACE}*\\d+${SPACE}*`, 'i');


/** Splits a label into its leading opening tag ("<p>" or "") and the rest. */
const splitLeadingTag = (label) => {
  const text = label == null ? '' : String(label);
  const match = text.match(LEADING_OPEN_TAG_REGEX);
  return match ? [match[0], text.slice(match[0].length)] : ['', text];
};

/** "Question 2 - Strengths" -> "Strengths", "<p>Question 2 - Strengths</p>" -> "<p>Strengths</p>". */
export const stripQuestionPrefix = (label) => {
  if (label == null) return label;
  const [tag, rest] = splitLeadingTag(label);
  return `${tag}${rest.replace(EXISTING_PREFIX_REGEX, '')}`;
};

/**
 * "Strengths" -> "Question 2 - Strengths" (an existing "Question N - " prefix is replaced).
 * Labels come from a rich text editor and may be HTML ("<p>Strengths</p>"); the prefix is
 * placed inside the first tag so it renders on the same line as the label.
 */
export const prefixQuestionLabel = (label, number) => {
  const [tag, rest] = splitLeadingTag(stripQuestionPrefix(label));
  return `${tag}${QUESTION_PREFIX}:${number} ${rest}`;
};

/**
 * Returns a copy of the form data with the first MAX_QUESTIONS Text Area labels prefixed
 * "Question 1 - " .. "Question 5 - ". Text Areas past the limit lose any stale prefix
 * (e.g. one moved from position 2 to position 6). Non-array input is returned as is.
 */
export const applyQuestionNumbering = (data) => {
  if (!Array.isArray(data)) return data;
  let count = 0;
  return data.map((element) => {
    if (!isTextArea(element)) return element;
    if (count >= MAX_QUESTIONS) {
      const label = stripQuestionPrefix(element.label);
      return label === element.label ? element : { ...element, label };
    }
    count += 1;
    return { ...element, label: prefixQuestionLabel(element.label, count) };
  });
};